// Region + language picker.
//
// Two orthogonal concepts:
//   - LANGUAGE (copy locale) is owned by the URL path prefix (see scripts/i18n).
//     Choosing a language navigates to that locale URL — the dictionaries and
//     <html lang>/hreflang follow from the URL, which is what keeps SEO correct.
//   - REGION (north-america | brazil | europe) drives currency + which
//     integration-logo cluster shows. It is a localStorage refinement, defaulted
//     from the active locale and overridable in the picker (e.g. a Brazilian
//     agency that briefs international clients in English: region=brazil,
//     language=en-US).
//
// The picker groups languages under regions. North America is English-only;
// Brazil offers Brazilian Portuguese (default) + English; Europe offers British
// English (default) + Spanish, German and French. The Europe sub-list reuses the
// exact same inline expand UI originally built for Brazil.

import React from 'react';
import { getLocaleFromPath, withLocale, LOCALE_DEFAULT_REGION } from './i18n/index.js';
import { t } from './i18n/useT.js';

export const REGION_STORAGE_KEY = 'scopeworth_region';
export const LANGUAGE_STORAGE_KEY = 'scopeworth_language';
export const LOCALE_COOKIE = 'sw_locale';

// Languages (URL locales) offered per region, default first. We currently offer
// only English and Brazilian Portuguese, so Europe shows British English only —
// Spanish/German/French are translated but intentionally hidden (see
// ENABLED_LOCALES in ./i18n/index.js). Re-add 'es', 'de', 'fr' here to surface
// them again.
// Brazil's pt-BR pages are served by geo but remain noindex (HIDDEN_LOCALES).
// A stale stored 'brazil' region can't paint en-US pages with Brazilian currency
// and logos. The picker is retained; the shared header keeps it hidden.
export const REGION_LANGUAGES = {
  'north-america': ['en-US'],
  'brazil': ['pt-BR'],
  'europe': ['en-GB'],
};

// Endonyms — a language is shown in its own name regardless of UI locale.
export const LANGUAGE_LABELS = {
  'pt-BR': 'Português (Brasil)',
  'en-US': 'English (US)',
  'en-GB': 'English (UK)',
  'es': 'Español',
  'de': 'Deutsch',
  'fr': 'Français',
};

export const LANGUAGE_FLAGS = {
  'pt-BR': 'br',
  'en-US': 'us',
  'en-GB': 'gb',
  'es': 'es',
  'de': 'de',
  'fr': 'fr',
};

// English fallbacks for region chrome (the component renders via t(), which
// falls back to en-GB; these mirror that so external importers still work).
export const REGION_LABELS = {
  'north-america': 'North America',
  'brazil': 'Brazil',
  'europe': 'Europe',
};

const VALID_REGIONS = new Set(['north-america', 'brazil', 'europe']);
const REGION_ORDER = ['north-america', 'brazil', 'europe'];

// Active copy locale comes from the URL.
function activeLocale() {
  if (typeof window === 'undefined') return 'en-GB';
  return getLocaleFromPath(window.location.pathname).locale;
}

// Region = a stored override (only honoured if it actually offers the active
// locale, so a stale choice can't paint a German page with Brazilian context),
// else the locale's default region.
export function detectRegion() {
  const locale = activeLocale();
  let stored = null;
  try { stored = window.localStorage.getItem(REGION_STORAGE_KEY); } catch (_) { /* ignore */ }
  if (stored && VALID_REGIONS.has(stored) && (REGION_LANGUAGES[stored] || []).includes(locale)) {
    return stored;
  }
  return LOCALE_DEFAULT_REGION[locale] || 'europe';
}

// Provider-less store shared across every entry point via useSyncExternalStore.
const REGION_CHANGE_EVENT = 'scopeworth:region-change';

let currentRegion = null;
let currentLanguage = null;
const listeners = new Set();

function ensureInit() {
  if (currentRegion !== null) return;
  currentRegion = detectRegion();
  currentLanguage = activeLocale();
  if (typeof window !== 'undefined') {
    window.addEventListener(REGION_CHANGE_EVENT, (event) => {
      const detail = event.detail || {};
      let changed = false;
      if (detail.region && VALID_REGIONS.has(detail.region) && detail.region !== currentRegion) {
        currentRegion = detail.region; changed = true;
      }
      if (detail.language && detail.language !== currentLanguage) {
        currentLanguage = detail.language; changed = true;
      }
      if (changed) listeners.forEach(l => l());
    });
    window.addEventListener('storage', (event) => {
      if (event.key === REGION_STORAGE_KEY && event.newValue && VALID_REGIONS.has(event.newValue)) {
        if (event.newValue !== currentRegion) { currentRegion = event.newValue; listeners.forEach(l => l()); }
      }
    });
  }
}

function subscribe(listener) {
  ensureInit();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let cachedSnapshot = null;
function getSnapshot() {
  ensureInit();
  if (!cachedSnapshot || cachedSnapshot.region !== currentRegion || cachedSnapshot.language !== currentLanguage) {
    cachedSnapshot = { region: currentRegion, language: currentLanguage };
  }
  return cachedSnapshot;
}

const SERVER_SNAPSHOT = { region: 'europe', language: 'en-GB' };
function getServerSnapshot() { return SERVER_SNAPSHOT; }

// Update region (and optionally language) in the live store WITHOUT navigating.
// Used when the chosen locale is already the current URL — only the region
// context (currency/logos) needs to change.
export function setRegion(nextRegion, nextLanguage) {
  if (!VALID_REGIONS.has(nextRegion)) return;
  ensureInit();
  const language = nextLanguage || currentLanguage;
  try { window.localStorage.setItem(REGION_STORAGE_KEY, nextRegion); } catch (_) {}
  try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language); } catch (_) {}
  currentRegion = nextRegion;
  currentLanguage = language;
  cachedSnapshot = null;
  listeners.forEach(l => l());
  try {
    window.dispatchEvent(new CustomEvent(REGION_CHANGE_EVENT, { detail: { region: nextRegion, language } }));
  } catch (_) {}
}

// Persist a region+locale choice and, if the locale differs from the current
// URL, navigate to it (full load → exactly one locale per page). Persisting the
// sw_locale cookie lets the edge middleware honour the choice on later visits.
function selectLocale(nextRegion, nextLocale) {
  try { window.localStorage.setItem(REGION_STORAGE_KEY, nextRegion); } catch (_) {}
  try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLocale); } catch (_) {}
  try { document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(nextLocale)}; path=/; max-age=31536000; samesite=Lax`; } catch (_) {}
  const targetPath = withLocale(window.location.pathname, nextLocale);
  if (targetPath === window.location.pathname) {
    setRegion(nextRegion, nextLocale); // same URL — swap region context in place
    return false;
  }
  window.location.assign(targetPath + window.location.search + window.location.hash);
  return true;
}

export function RegionProvider({ children }) { ensureInit(); return children; }

export function useRegion() {
  const snap = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { region: snap.region, language: snap.language, setRegion };
}

function usePopover() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

function ChevronIcon({ size = 12, className = 'sw-region-toggle-chevron' }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6"/>
    </svg>
  );
}

function CheckIcon({ size = 13 }) {
  return (
    <svg className="sw-region-pop-check" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7"/>
    </svg>
  );
}

// Simplified, recognizable flag glyphs (rendered ~18×12; not literal proportions).
function Flag({ code, size = 18 }) {
  const h = Math.round(size * 0.66);
  const common = { className: 'sw-region-flag', width: size, height: h, viewBox: '0 0 28 20', 'aria-hidden': true };
  if (code === 'br') {
    return (
      <svg {...common}>
        <rect width="28" height="20" rx="2" fill="#009C3B"/>
        <path d="M14 2.5L25.5 10 14 17.5 2.5 10z" fill="#FFDF00"/>
        <circle cx="14" cy="10" r="3.6" fill="#002776"/>
        <path d="M10.6 9.2c2-1.1 4.4-1.1 6.7 0" stroke="#FFFFFF" strokeWidth="0.6" fill="none"/>
      </svg>
    );
  }
  if (code === 'us') {
    return (
      <svg {...common}>
        <rect width="28" height="20" rx="2" fill="#BF0A30"/>
        {[1, 3, 5, 7, 9, 11].map((y) => (
          <rect key={y} x="0" y={y * 1.538} width="28" height="1.538" fill="#FFFFFF"/>
        ))}
        <rect width="12" height="10" fill="#002868"/>
      </svg>
    );
  }
  if (code === 'gb') {
    return (
      <svg {...common}>
        <rect width="28" height="20" rx="2" fill="#012169"/>
        <path d="M0 0L28 20M28 0L0 20" stroke="#FFFFFF" strokeWidth="2.4"/>
        <path d="M0 0L28 20M28 0L0 20" stroke="#C8102E" strokeWidth="1.2"/>
        <path d="M14 0V20M0 10H28" stroke="#FFFFFF" strokeWidth="3.6"/>
        <path d="M14 0V20M0 10H28" stroke="#C8102E" strokeWidth="2"/>
      </svg>
    );
  }
  if (code === 'es') {
    // Spain: red-yellow-red horizontal bands, yellow band wider.
    return (
      <svg {...common}>
        <rect width="28" height="20" rx="2" fill="#AA151B"/>
        <rect y="5" width="28" height="10" fill="#F1BF00"/>
      </svg>
    );
  }
  if (code === 'de') {
    // Germany: black / red / gold horizontal thirds.
    return (
      <svg {...common}>
        <rect width="28" height="20" rx="2" fill="#000000"/>
        <rect y="6.67" width="28" height="6.67" fill="#DD0000"/>
        <rect y="13.33" width="28" height="6.67" fill="#FFCE00"/>
      </svg>
    );
  }
  if (code === 'fr') {
    // France: blue / white / red vertical thirds.
    return (
      <svg {...common}>
        <rect width="28" height="20" rx="2" fill="#FFFFFF"/>
        <rect width="9.33" height="20" fill="#0055A4"/>
        <rect x="18.67" width="9.33" height="20" fill="#EF4135"/>
      </svg>
    );
  }
  return null;
}

// Region picker — region rows, each expanding inline to its language sub-list
// when it offers more than one language (Brazil, Europe).
export function RegionPicker({ variant = 'nav', className = '' }) {
  const { region } = useRegion();
  const language = activeLocale();
  const { open, setOpen, ref } = usePopover();
  const [expanded, setExpanded] = React.useState(null);
  const count = REGION_ORDER.length;

  React.useEffect(() => { if (!open) setExpanded(null); }, [open]);

  const wrapClass = [
    'sw-region-pop-wrap',
    variant === 'mobile' ? 'sw-region-pop-wrap--mobile' : 'sw-region-pop-wrap--nav',
    className,
  ].filter(Boolean).join(' ');

  const onRowClick = (r) => {
    const languages = REGION_LANGUAGES[r] || [];
    if (languages.length > 1) {
      setExpanded((cur) => (cur === r ? null : r));
      return;
    }
    if (!selectLocale(r, languages[0])) setOpen(false);
  };

  const onLanguageClick = (r, lang) => {
    if (!selectLocale(r, lang)) setOpen(false);
  };

  return (
    <div className={wrapClass} ref={ref}>
      <button
        type="button"
        className={`sw-region-toggle ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('nav.selectRegion')}
        title={t('nav.switchRegion')}
      >
        <span className={`sw-region-toggle-status sw-region-dot--${region}`} aria-hidden="true" />
        <span className="sw-region-toggle-name">{t(`region.labels.${region}`)}</span>
        <span className="sw-region-toggle-count">{count}</span>
        <ChevronIcon size={12} />
      </button>
      {open ? (
        <div className="sw-region-pop" role="listbox" aria-label={t('region.ariaList')}>
          <div className="sw-region-pop-head">
            <div className="sw-region-pop-eye">{t('region.eyebrow', { count })}</div>
          </div>
          <div className="sw-region-pop-list">
            {REGION_ORDER.map((r) => {
              const isActive = r === region;
              const languages = REGION_LANGUAGES[r] || [];
              const hasMultiLanguage = languages.length > 1;
              const isExpanded = expanded === r;
              return (
                <React.Fragment key={r}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    aria-expanded={hasMultiLanguage ? isExpanded : undefined}
                    className={`sw-region-pop-row ${isActive ? 'is-active' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                    onClick={() => onRowClick(r)}
                  >
                    <span className={`sw-region-pop-row-status sw-region-dot--${r}`} aria-hidden="true" />
                    <div>
                      <div className="sw-region-pop-row-t">{t(`region.labels.${r}`)}</div>
                      <div className="sw-region-pop-row-m">{t(`region.meta.${r}`)}</div>
                    </div>
                    {hasMultiLanguage ? (
                      <ChevronIcon size={12} className={`sw-region-pop-row-chevron ${isExpanded ? 'is-open' : ''}`} />
                    ) : isActive ? (
                      <CheckIcon size={13} />
                    ) : (
                      <span aria-hidden="true" />
                    )}
                  </button>
                  {hasMultiLanguage && isExpanded ? (
                    <div className="sw-region-pop-sublist" role="group" aria-label={t(`region.labels.${r}`)}>
                      {languages.map((lang) => {
                        const isLangActive = isActive && language === lang;
                        return (
                          <button
                            key={lang}
                            type="button"
                            role="option"
                            aria-selected={isLangActive}
                            className={`sw-region-pop-subrow ${isLangActive ? 'is-active' : ''}`}
                            onClick={() => onLanguageClick(r, lang)}
                          >
                            <Flag code={LANGUAGE_FLAGS[lang]} size={20} />
                            <div className="sw-region-pop-subrow-t">{LANGUAGE_LABELS[lang]}</div>
                            <span className="sw-region-pop-subrow-code">{lang}</span>
                            {isLangActive ? <CheckIcon size={13} /> : <span aria-hidden="true" />}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>
          <div className="sw-region-pop-foot">
            <span className="sw-region-pop-legend">
              <span className="sw-region-pop-legend-dot sw-region-dot--north-america" />{t('region.labels.north-america')}
            </span>
            <span className="sw-region-pop-legend">
              <span className="sw-region-pop-legend-dot sw-region-dot--brazil" />{t('region.labels.brazil')}
            </span>
            <span className="sw-region-pop-legend">
              <span className="sw-region-pop-legend-dot sw-region-dot--europe" />{t('region.labels.europe')}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function useActiveRegion() {
  return useRegion().region;
}
